/**
 * Quotes tab formatting — Cancer.
 *
 * Pure display formatters for Cancer fields, derived from FIELD_RULES.md
 * (propelx_mapping_rules_csv/FIELD_RULES.md → Cancer). These are scoped to
 * the Quotes tab and are NOT used by `@platform/btx-plan-designer` (which
 * has its own valueFormatters for the BTX plan editor).
 *
 * Each formatter takes a `row` (a v5-shaped proposal benefit row) and
 * returns the string to display in the Quotes grid cell. Sentinels come
 * first; otherwise we read the discriminator type and any companion
 * amount(s) from the row.
 *
 * The formatters intentionally do not depend on AG Grid; they accept a
 * plain object so they can be unit-tested and reused (export, summaries,
 * AI-extraction previews, etc.).
 */

import {
  formatPreExistingConditionMonthsDetail,
  formatRateGuarantee,
} from "./shared";

const NOT_INCLUDED = "Not Included";
const NOT_OFFERED = "Not Offered";
const INCLUDED = "Included";

const SENTINEL_LABELS: Record<string, string> = {
  notDisclosed: "Not Disclosed",
  notIncluded: NOT_INCLUDED,
  notOffered: NOT_OFFERED,
  notApplicable: "Not Applicable",
};

const isSentinel = (value: unknown): value is keyof typeof SENTINEL_LABELS =>
  typeof value === "string" && value in SENTINEL_LABELS;

const sentinelLabel = (value: string): string => SENTINEL_LABELS[value] ?? "";

const currency0 = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatCurrency = (value: unknown): string => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  return currency0.format(num);
};

const isEmpty = (value: unknown): boolean =>
  value === undefined || value === null || value === "";

/** A formatter receives the v5 benefit row and returns the cell string. */
export type CancerFieldFormatter = (row: Record<string, unknown>) => string;

/**
 * Generic typed-amount formatter.
 *
 *   <type>                     → render
 *   `dollarAmount`             → `$X`
 *   <sentinel>                 → sentinel label
 *
 * Used for fields whose only non-sentinel value is `dollarAmount`:
 * `diagnosisBenefit`, `radiationTherapyChemo`, `bloodPlasmaPlatelets`,
 * `hospitalConfinementInitial`, `icuConfinementInitial`, `wellnessBenefit`.
 */
const dollarAmountTyped =
  (typeKey: string, amountKey: string): CancerFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type);
    if (type === "dollarAmount") {
      const amount = formatCurrency(row[amountKey]);
      return amount || "";
    }
    return "";
  };

/**
 * `Schedule up to $X` formatter (skinCancer, surgicalBenefit).
 */
const scheduleUpToDollar =
  (typeKey: string, amountKey: string): CancerFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type);
    if (type === "scheduleUpToDollarAmount") {
      const amount = formatCurrency(row[amountKey]);
      return amount ? `Schedule up to ${amount}` : "";
    }
    return "";
  };

/** Hospice — display amount for dollarAmount; amount + duration for dollarAmountPerDayPerLifetime. */
const formatHospice: CancerFieldFormatter = (row) => {
  const type = row["hospiceType"];
  if (isSentinel(type)) return sentinelLabel(type);
  if (type === "dollarAmount") {
    return formatCurrency(row["hospiceAmount"]) || "";
  }
  if (type === "dollarAmountPerDayPerLifetime") {
    const amount = formatCurrency(row["hospiceAmount"]);
    const duration = row["hospiceDuration"];
    if (!amount) return "";
    if (!isEmpty(duration) && Number.isFinite(Number(duration))) {
      return `${amount}/day · ${String(duration)} days`;
    }
    return `${amount}/day`;
  }
  return "";
};

/**
 * Initial Diagnosis Waiting Period.
 *
 * FIELD_RULES.md flags the value set as OPEN; we render
 * `${duration} ${unit}` for the known `days` / `months` types and fall
 * back to the duration alone otherwise. Sentinels render as labels.
 */
const formatInitialDiagnosisWaitingPeriod: CancerFieldFormatter = (row) => {
  const type = row["initialDiagnosisWaitingPeriodType"];
  if (isSentinel(type)) return sentinelLabel(type);

  const duration = row["initialDiagnosisWaitingPeriodDuration"];
  if (isEmpty(duration)) return "";
  if (type === "days") return `${String(duration)} days`;
  if (type === "months") return `${String(duration)} months`;
  return String(duration);
};

/**
 * Pre-existing condition — slash-joined month fraction or sentinel label.
 *
 * When type = `months`, the companion `preExistingConditionMonthsDetail`
 * field holds the already-formatted "XX/XX" or "XX/XX/XX" string.
 * `waived` / `none` render as their literals; standard sentinels apply.
 */
const formatPreExistingCondition: CancerFieldFormatter = (row) => {
  const type = row["preExistingConditionType"];
  if (isSentinel(type)) return sentinelLabel(type);
  if (type === "waived") return "Waived";
  if (type === "none") return "None";
  if (type !== "months") return "";

  const detail = row["preExistingConditionMonthsDetail"];
  return typeof detail === "string" && detail
    ? formatPreExistingConditionMonthsDetail(detail)
    : "";
};

const ENUM_LABELS_INCLUSION: Record<string, string> = {
  included: INCLUDED,
  notIncluded: NOT_INCLUDED,
  notOffered: NOT_OFFERED,
};

const ENUM_LABELS_ANNUAL_OPEN_ENROLLMENT: Record<string, string> = {
  oneTimeOpenEnrollment: "One Time Open Enrollment",
  annualOpenEnrollment: "Annual Open Enrollment",
  modifiedOpenEnrollment: "Modified Open Enrollment",
  included: INCLUDED,
  notIncluded: NOT_INCLUDED,
  notOffered: NOT_OFFERED,
};

const enumField =
  (fieldKey: string, labels: Record<string, string>): CancerFieldFormatter =>
  (row) => {
    const value = row[fieldKey];
    if (typeof value !== "string" || value === "") return "";
    if (isSentinel(value)) return sentinelLabel(value);
    return labels[value] ?? "";
  };

/**
 * Cancer field registry — maps the v5 field id (camelCase) to its
 * formatter. Only fields with non-trivial display rules are listed here;
 * anything not in the registry should fall through to whatever the
 * grid's default rendering is.
 */
export const cancerFormatters: Record<string, CancerFieldFormatter> = {
  diagnosisBenefit: dollarAmountTyped(
    "diagnosisBenefitType",
    "diagnosisBenefitAmount",
  ),
  initialDiagnosisWaitingPeriod: formatInitialDiagnosisWaitingPeriod,
  radiationTherapyChemo: dollarAmountTyped(
    "radiationTherapyChemoType",
    "radiationTherapyChemoAmount",
  ),
  bloodPlasmaPlatelets: dollarAmountTyped(
    "bloodPlasmaPlateletsType",
    "bloodPlasmaPlateletsAmount",
  ),
  hospice: formatHospice,
  hospitalConfinementInitial: dollarAmountTyped(
    "hospitalConfinementInitialType",
    "hospitalConfinementInitialAmount",
  ),
  icuConfinementInitial: dollarAmountTyped(
    "icuConfinementInitialType",
    "icuConfinementInitialAmount",
  ),
  skinCancer: scheduleUpToDollar("skinCancerType", "skinCancerAmount"),
  surgicalBenefit: scheduleUpToDollar(
    "surgicalBenefitType",
    "surgicalBenefitAmount",
  ),
  preExistingCondition: formatPreExistingCondition,
  portability: enumField("portability", ENUM_LABELS_INCLUSION),
  wellnessBenefit: dollarAmountTyped(
    "wellnessBenefitType",
    "wellnessBenefitAmount",
  ),
  waiverOfPremium: enumField("waiverOfPremium", ENUM_LABELS_INCLUSION),
  annualOpenEnrollment: enumField(
    "annualOpenEnrollment",
    ENUM_LABELS_ANNUAL_OPEN_ENROLLMENT,
  ),
  rateGuarantee: formatRateGuarantee,
};

/**
 * Format a Cancer field for the Quotes tab. Returns `undefined` when the
 * field has no formatter (the caller should fall back to the default
 * cell rendering).
 */
export const formatCancerField = (
  fieldId: string,
  row: Record<string, unknown>,
): string | undefined => {
  const formatter = cancerFormatters[fieldId];
  return formatter ? formatter(row) : undefined;
};
